/**
 * Serializes NotebookTabContent to Jupyter Notebook v4 (.ipynb) JSON format.
 *
 * Supports two save modes:
 * - 'rich': includes cell outputs (result sets, messages, timing)
 * - 'clean': strips all outputs for clean version control diffs
 */

import type {
  NotebookTabContent,
  NotebookCell,
  NotebookCodeCell,
  NotebookJavaScriptCell,
  NotebookSaveMode,
  CellOutput,
} from '../types/notebookTypes';
import type { QueryResultSet } from '../../core/types/workspaceTypes';

// ============================================================================
// .ipynb Types (Jupyter Notebook v4)
// ============================================================================

type IpynbNotebook = {
  nbformat: 4;
  nbformat_minor: 2;
  metadata: IpynbMetadata;
  cells: IpynbCell[];
  /** Preserve unknown top-level fields on round-trip */
  [key: string]: unknown;
};

type IpynbMetadata = {
  kernelspec: { name: string; display_name: string };
  language_info: { name: string };
  jam_sql?: {
    version: number;
    saveMode?: NotebookSaveMode;
    connectionName?: string;
    engine?: string;
  };
  /** Preserve unknown metadata fields on round-trip */
  [key: string]: unknown;
};

type IpynbCell = {
  cell_type: 'code' | 'markdown' | 'raw';
  id?: string;
  source: string[];
  metadata: Record<string, unknown>;
  outputs?: IpynbOutput[];
  execution_count?: number | null;
  /** Preserve unknown cell fields on round-trip */
  [key: string]: unknown;
};

type IpynbOutput = {
  output_type: string;
  data?: Record<string, unknown>;
  text?: string[];
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
};

// ============================================================================
// Serialization
// ============================================================================

/**
 * Serialize notebook content to .ipynb JSON string.
 */
export function serializeToIpynb(
  content: NotebookTabContent,
  saveMode: NotebookSaveMode = 'rich',
  /** Preserved fields from a previously loaded .ipynb (for round-trip) */
  preservedNotebook?: Record<string, unknown>
): string {
  const notebook: IpynbNotebook = {
    ...(preservedNotebook ?? {}),
    nbformat: 4,
    nbformat_minor: 2,
    metadata: {
      ...(preservedNotebook?.metadata as Record<string, unknown> | undefined),
      kernelspec: { name: 'sql', display_name: 'SQL' },
      language_info: { name: 'sql' },
      jam_sql: {
        version: content.metadata.version,
        saveMode,
        ...(content.metadata.connectionName ? { connectionName: content.metadata.connectionName } : {}),
        ...(content.metadata.engine ? { engine: content.metadata.engine } : {}),
      },
    },
    cells: content.cells.map((cell) => serializeCell(cell, saveMode)),
  };

  return JSON.stringify(notebook, null, 1);
}

function serializeCell(cell: NotebookCell, saveMode: NotebookSaveMode): IpynbCell {
  const isJs = cell.cellType === 'javascript';
  const chartConfig = (cell.cellType === 'code' || cell.cellType === 'javascript') && 'chartConfig' in cell
    ? cell.chartConfig : undefined;
  const jamSqlMeta: Record<string, unknown> = {
    ...(isJs ? { language: 'javascript' } : {}),
    ...(chartConfig ? { chartConfig } : {}),
  };
  const base: IpynbCell = {
    cell_type: isJs ? 'code' : cell.cellType === 'code' ? 'code' : 'markdown',
    id: cell.id,
    source: splitSource(cell.source),
    metadata: Object.keys(jamSqlMeta).length > 0 ? { jam_sql: jamSqlMeta } : {},
  };

  if (cell.cellType === 'code' || cell.cellType === 'javascript') {
    const execCell = cell as NotebookCodeCell | NotebookJavaScriptCell;
    base.execution_count = execCell.executionOrder ?? null;
    base.outputs = saveMode === 'rich' ? serializeExecutableOutputs(execCell) : [];
  }

  return base;
}

function splitSource(source: string): string[] {
  if (!source) return [];
  const lines = source.split('\n');
  // .ipynb convention: all lines except the last end with '\n'
  return lines.map((line, i) => (i < lines.length - 1 ? line + '\n' : line));
}

/**
 * Serialize executable cell outputs for 'rich' save mode.
 * Dual MIME type: `application/vnd.jam-sql.result+json` (structured) + `text/plain` (summary).
 */
function serializeExecutableOutputs(cell: NotebookCodeCell | NotebookJavaScriptCell): IpynbOutput[] {
  if (!cell.outputs) return [];
  const outputs: IpynbOutput[] = [];

  // Result sets as execute_result outputs
  for (const rs of cell.outputs.resultSets) {
    outputs.push(serializeResultSet(rs, cell.outputs));
  }

  // Messages as stream outputs
  if (cell.outputs.messages.length > 0) {
    const messageLines = cell.outputs.messages.map(
      (m) => `[${m.type.toUpperCase()}] ${m.content}\n`
    );
    outputs.push({
      output_type: 'stream',
      name: 'stderr',
      text: messageLines,
    });
  }

  return outputs;
}

function serializeResultSet(rs: QueryResultSet, output: CellOutput): IpynbOutput {
  // Rows are keyed by the column `id` when the producer set one (duplicate or
  // blank names get a positional id), else by name — same rule as the grid.
  const keys = rs.columns.map((c) => c.id ?? c.name);
  const plainRows = rs.rows.slice(0, 20).map((row) =>
    keys.map((key) => String(row[key] ?? '')).join('\t')
  );
  const plainSummary = [
    rs.columns.map((c) => c.name).join('\t') + '\n',
    ...plainRows.map((r) => r + '\n'),
    ...(rs.rows.length > 20 ? [`... (${rs.rows.length} rows total)\n`] : []),
    ...(output.durationMs != null ? [`Completed in ${output.durationMs}ms\n`] : []),
  ];

  return {
    output_type: 'execute_result',
    data: {
      'application/vnd.jam-sql.result+json': {
        // `id` is persisted only when it differs from the name, so a saved
        // duplicate-named or unnamed column still addresses its own row values
        // when the notebook is reopened.
        columns: rs.columns.map((c) => ({
          ...(c.id !== undefined && c.id !== c.name ? { id: c.id } : {}),
          name: c.name,
          dataType: c.dataType,
        })),
        rows: rs.rows,
        totalRowCount: output.totalRowCount ?? rs.rows.length,
        durationMs: output.durationMs,
      },
      'text/plain': plainSummary,
    },
    metadata: {},
    execution_count: null,
  };
}

/**
 * Compute a content hash for dirty tracking.
 * Hashes the cell sources and metadata (ignores ephemeral state like outputs).
 */
export function computeNotebookHash(content: NotebookTabContent): string {
  const hashable = content.cells.map((c) => {
    const chartPart = (c.cellType === 'code' || c.cellType === 'javascript') && 'chartConfig' in c && c.chartConfig
      ? JSON.stringify(c.chartConfig) : '';
    return `${c.cellType}:${c.source}:${chartPart}`;
  }).join('\0');
  return fnv1aHash(hashable);
}

function fnv1aHash(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}
